import React from 'react';
import { useTranslation } from 'react-i18next';

import { PartDemand } from '../../services/partDemandTypes';
import { DataTable } from '../DataTable';
import { formatDate, percent } from '../quoteToCash/format';

import { customerLabel, DEMAND_COLORS, formatQty } from './format';

const TOP = 10;

/**
 * Customers by demand over the analysis period, each ship-to as its own customer: the top 10 as bars (the rest as
 * Other), then every one
 */
export const CustomerDemandSection: React.FC<{ data: PartDemand }> = ({
  data,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const qty = (value: number) => formatQty(value, data.part.uom, lang);
  const buyers = data.customers.filter((c) => c.demandQty > 0);
  const rest = buyers.slice(TOP);
  const bars = [
    ...buyers.slice(0, TOP).map((c) => ({
      key: `${c.custNum}/${c.shipToNum}`,
      name: customerLabel(c),
      qty: c.demandQty,
      pct: c.pctOfCustomerDemand,
    })),
    ...(rest.length > 0
      ? [
          {
            key: 'other',
            name: t('partDemand.customers.other', { count: rest.length }),
            qty: rest.reduce((total, c) => total + c.demandQty, 0),
            pct: rest.reduce((total, c) => total + c.pctOfCustomerDemand, 0),
          },
        ]
      : []),
  ];
  const largest = Math.max(1, ...bars.map((b) => b.qty));

  if (data.customers.length === 0) {
    return (
      <p className="text-sm text-gray-600 py-6 text-center">
        {t('partDemand.kpi.noCustomerDemand')}
      </p>
    );
  }

  return (
    <>
      <ul
        className="space-y-1.5 mb-6"
        aria-label={t('partDemand.customers.chart')}
      >
        {bars.map((bar) => (
          <li
            key={bar.key}
            className="grid grid-cols-[minmax(8rem,16rem)_1fr_auto] items-center gap-3 text-sm"
          >
            <span className="truncate text-gray-700" title={bar.name}>
              {bar.name}
            </span>
            <span className="h-4 bg-gray-100 rounded-sm">
              <span
                className="block h-4 rounded-sm"
                style={{
                  width: `${(bar.qty / largest) * 100}%`,
                  background:
                    bar.key === 'other'
                      ? DEMAND_COLORS.returns
                      : DEMAND_COLORS.customer,
                }}
              />
            </span>
            <span className="tabular-nums text-gray-900 w-28 text-right">
              {qty(bar.qty)} · {percent(bar.pct, lang, 0)}
            </span>
          </li>
        ))}
      </ul>
      <DataTable
        data={data.customers}
        keyExtractor={(row) => `${row.custNum}/${row.shipToNum}`}
        columns={[
          { key: 'customerName', header: t('partDemand.customers.name') },
          {
            key: 'shipToName',
            header: t('partDemand.customers.shipTo'),
            render: (value, row) =>
              (value as string) ||
              (row?.shipToNum
                ? row.shipToNum
                : t('partDemand.customers.noShipTo')),
          },
          {
            key: 'demandQty',
            header: t('partDemand.customers.demand'),
            align: 'right',
            render: (value) => qty(value as number),
          },
          {
            key: 'returned',
            header: t('partDemand.customers.returned'),
            align: 'right',
            render: (value) =>
              (value as number) > 0 ? qty(value as number) : '',
          },
          {
            key: 'demandEvents',
            header: t('partDemand.customers.events'),
            align: 'right',
          },
          {
            key: 'activeMonths',
            header: t('partDemand.customers.activeMonths'),
            align: 'right',
          },
          {
            key: 'pctOfCustomerDemand',
            header: t('partDemand.customers.share'),
            align: 'right',
            render: (value) => percent(value as number, lang, 1),
          },
          {
            key: 'lastDemandDate',
            header: t('partDemand.customers.lastDemand'),
            render: (value) =>
              value ? formatDate(value as string, lang, 'long') : '',
          },
        ]}
      />
    </>
  );
};
